import React, { useState, useEffect, useCallback, useRef } from 'react'
import Swal from 'sweetalert2'
import { POST, GET } from '../../../services/api'
import './SaleDatesLoteModal.css'

interface ProductWithSaleDates {
  id: number;
  code: string;
  name: string;
  categoryName: string;
  saleDateStart: string | null;
  saleDateEnd: string | null;
  selected?: boolean;
}

interface SaleDatesLoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  pluginspaceId: number;
}

// Funções de conversão - mesmo padrão do create.tsx e update.tsx
const convertMaskedToISO = (maskedDate: string): string | null => {
  if (!maskedDate || maskedDate.trim() === '') return null
  
  const cleaned = maskedDate.trim()
  if (cleaned.length < 16) return null
  
  try {
    const parts = cleaned.match(/(\d{2})\/(\d{2})\/(\d{4})\s(\d{2}):(\d{2})/)
    
    if (!parts || parts.length < 6) return null
    
    const day = parts[1]
    const month = parts[2]
    const year = parts[3]
    const hour = parts[4]
    const minute = parts[5]
    
    const dayNum = parseInt(day)
    const monthNum = parseInt(month)
    const yearNum = parseInt(year)
    const hourNum = parseInt(hour)
    const minuteNum = parseInt(minute)
    
    if (dayNum < 1 || dayNum > 31 || monthNum < 1 || monthNum > 12 || 
        hourNum < 0 || hourNum > 23 || minuteNum < 0 || minuteNum > 59) {
      return null
    }
    
    // Cria data SEM forçar UTC - usa o timezone local do navegador
    const date = new Date(yearNum, monthNum - 1, dayNum, hourNum, minuteNum, 0)
    
    if (isNaN(date.getTime())) return null
    
    return date.toISOString()
  } catch (error) {
    console.error('Erro ao converter data:', error)
    return null
  }
}

const convertISOToMasked = (isoDate: string): string => {
  if (!isoDate) return ''
  try {
    const date = new Date(isoDate)
    if (isNaN(date.getTime())) return ''
    
    // Usa o timezone local do navegador para exibir
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    const hour = String(date.getHours()).padStart(2, '0')
    const minute = String(date.getMinutes()).padStart(2, '0')
    
    return `${day}/${month}/${year} ${hour}:${minute}`
  } catch (error) {
    console.error('Erro ao converter data para máscara:', error)
    return ''
  }
}

// Função para aplicar máscara DD/MM/YYYY HH:MM enquanto digita
const applyDateTimeMask = (value: string): string => {
  // Remove caracteres que não são dígitos
  let cleaned = value.replace(/\D/g, '')
  
  // Limita a 12 dígitos (DDMMYYYYHHMM)
  cleaned = cleaned.substring(0, 12)
  
  // Aplica a máscara
  let masked = ''
  
  if (cleaned.length >= 1) masked = cleaned.substring(0, 2)
  if (cleaned.length >= 3) masked += '/' + cleaned.substring(2, 4)
  if (cleaned.length >= 5) masked += '/' + cleaned.substring(4, 8)
  if (cleaned.length >= 9) masked += ' ' + cleaned.substring(8, 10)
  if (cleaned.length >= 11) masked += ':' + cleaned.substring(10, 12)
  
  return masked
}

export const SaleDatesLoteModal: React.FC<SaleDatesLoteModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  pluginspaceId,
}) => {
  // Estados de filtro
  const [filterCode, setFilterCode] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // Estados de dados
  const [products, setProducts] = useState<ProductWithSaleDates[]>([]);
  const [loading, setLoading] = useState(false);
  const [allSelected, setAllSelected] = useState(false);
  const [currentPage, setCurrentPage] = useState(1)
  const [pageCount, setPageCount] = useState(1)
  const [itemCount, setItemCount] = useState(0)
  const [hasPreviousPage, setHasPreviousPage] = useState(false)
  const [hasNextPage, setHasNextPage] = useState(false)

  // Estados de edição
  const [editMode, setEditMode] = useState<'individual' | 'batch'>('batch');
  const [batchDateStart, setBatchDateStart] = useState('')
  const [batchDateEnd, setBatchDateEnd] = useState('')

  // Refs para debounce
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)









  // Buscar produtos com filtros (com debounce)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchProducts = useCallback(async (page: number = 1) => {
    if (!pluginspaceId) {
      console.warn('pluginspaceId não definido:', pluginspaceId)
      return
    }

    setLoading(true)
    
    console.log('🔍 Iniciando busca com filtros:', {
      pluginspaceId,
      code: filterCode || undefined,
      name: filterName || undefined,
      categoryName: filterCategory || undefined,
    })

    try {
      const response = await POST(`/product/filter-sale-dates?page=${page}&take=${50}`, {
        pluginspaceId,
        code: filterCode || undefined,
        name: filterName || undefined,
        categoryName: filterCategory || undefined,
      })

      console.log('✅ Resposta da API:', response)
      console.log('📊 Estrutura da resposta - response.data:', response?.data)
      console.log('📊 Estrutura - response.data?.data:', response?.data?.data)

      // A resposta pode vir diretamente em response.data ou em response.data.data
      const productsList = response?.data?.data || response?.data || []
      const metaData = response?.data?.meta || response?.meta || {}
      
      if (Array.isArray(productsList) && productsList.length > 0) {
        const productsData = productsList.map(
          (p: any) => ({
            id: p.id,
            code: p.code,
            name: p.name,
            categoryName: p.classification?.name || '-',
            // Converte ISO para máscara DD/MM/YYYY HH:MM
            saleDateStart: p.saleDateStart ? convertISOToMasked(p.saleDateStart) : '',
            saleDateEnd: p.saleDateEnd ? convertISOToMasked(p.saleDateEnd) : '',
            selected: false,
          }),
        )
        console.log('📦 Produtos carregados:', productsData)
        setProducts(productsData)
      } else {
        console.warn('⚠️ Resposta não contém dados:', { response, productsList })
        setProducts([])
      } 

      const nextPage = Number(metaData.page ?? page)
      const nextPageCount = Number(metaData.pageCount ?? 1)
      const nextItemCount = Number(metaData.itemCount ?? 0)

      setCurrentPage(Number.isNaN(nextPage) ? page : nextPage)
      setPageCount(Number.isNaN(nextPageCount) ? 1 : nextPageCount)
      setItemCount(Number.isNaN(nextItemCount) ? 0 : nextItemCount)
      setHasPreviousPage(Boolean(metaData.hasPreviousPage))
      setHasNextPage(Boolean(metaData.hasNextPage))
    } catch (error: any) {
      console.error('❌ Erro ao buscar produtos:', error)
      Swal.fire('Erro', 'Falha ao buscar produtos', 'error')
      console.error('Erro ao buscar produtos:', error)
    } finally {
      setLoading(false)
    }
  }, [pluginspaceId, filterCode, filterName, filterCategory])



  // Disparar busca quando clicar no botão Pesquisar
  const handleSearch = () => {
    // Validar se Nome tem 3+ caracteres quando preenchido
    if (filterName.trim().length > 0 && filterName.trim().length < 3) {
      Swal.fire('Aviso', 'Nome deve ter no mínimo 3 caracteres', 'warning')
      return
    }

    // Validar se Categoria tem 3+ caracteres quando preenchido
    if (filterCategory.trim().length > 0 && filterCategory.trim().length < 3) {
      Swal.fire('Aviso', 'Categoria deve ter no mínimo 3 caracteres', 'warning')
      return
    }

    searchProducts(1);
  }

  // Ao abrir o modal, fazer busca inicial
  useEffect(() => {
    if (isOpen) {
      searchProducts();
    }
  }, [isOpen, searchProducts]);

  // Cleanup debounce
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Toggle seleção individual
  const toggleProductSelection = (index: number) => {
    const newProducts = [...products];
    newProducts[index].selected = !newProducts[index].selected;
    setProducts(newProducts);

    // Atualizar "Selecionar Tudo"
    const allSelectedCheck = newProducts.every((p) => p.selected);
    setAllSelected(allSelectedCheck);
  };

  // Selecionar/Desselecionar tudo
  const toggleSelectAll = () => {
    const newProducts = products.map((p) => ({
      ...p,
      selected: !allSelected,
    }));
    setProducts(newProducts);
    setAllSelected(!allSelected);
  };

  // Editar data individual
  const updateProductDate = (
    index: number,
    field: 'saleDateStart' | 'saleDateEnd',
    value: string,
  ) => {
    const newProducts = [...products];
    newProducts[index][field] = value;
    setProducts(newProducts);
  };

  // Atualizar datas
  const updateDates = async () => {
    const selectedProducts = products.filter((p) => p.selected)

    if (selectedProducts.length === 0) {
      Swal.fire('Aviso', 'Selecione pelo menos um produto', 'warning')
      return
    }

    // Validação de datas
    if (editMode === 'batch') {
      if (batchDateStart && batchDateEnd) {
        const start = new Date(batchDateStart)
        const end = new Date(batchDateEnd)
        if (start > end) {
          Swal.fire(
            'Erro',
            'Data de início não pode ser maior que data de término',
            'error',
          )
          return
        }
      }
    } else {
      // Validação individual
      for (const product of selectedProducts) {
        if (product.saleDateStart && product.saleDateEnd) {
          const start = new Date(product.saleDateStart)
          const end = new Date(product.saleDateEnd)
          if (start > end) {
            Swal.fire(
              'Erro',
              `Produto ${product.code}: data de início não pode ser maior que data de término`,
              'error',
            )
            return
          }
        }
      }
    }

    setLoading(true)
    try {
      // Converter datas para ISO usando a mesma função que create.tsx e update.tsx
      const startISO = editMode === 'batch' ? convertMaskedToISO(batchDateStart) : undefined
      const endISO = editMode === 'batch' ? convertMaskedToISO(batchDateEnd) : undefined

      console.log('📤 Enviando atualização em modo', editMode);
      console.log('  batchDateStart (máscara):', batchDateStart);
      console.log('  startISO (convertido):', startISO);
      console.log('  batchDateEnd (máscara):', batchDateEnd);
      console.log('  endISO (convertido):', endISO);

      const updatePayload = {
        pluginspaceId,
        productIds: selectedProducts.map((p) => p.id),
        saleDateStart: startISO || undefined,
        saleDateEnd: endISO || undefined,
      }

      console.log('📋 updatePayload final:', updatePayload);

      // Se modo individual, enviar cada produto separadamente
      if (editMode === 'individual') {
        for (const product of selectedProducts) {
          const startISO = convertMaskedToISO(product.saleDateStart || '')
          const endISO = convertMaskedToISO(product.saleDateEnd || '')
          
          console.log(`📤 Atualizando produto ${product.id}:`, {
            saleDateStart: startISO,
            saleDateEnd: endISO,
          });
          
          await POST('/product/update-sale-dates-batch', {
            pluginspaceId,
            productIds: [product.id],
            saleDateStart: startISO,
            saleDateEnd: endISO,
          })
        }
      } else {
        await POST('/product/update-sale-dates-batch', updatePayload)
      }

      Swal.fire(
        'Sucesso',
        `${selectedProducts.length} produto(s) atualizado(s)`,
        'success',
      )

      onSuccess()
      resetModal()
      onClose()
    } catch (error: any) {
      console.error('❌ Erro ao atualizar datas:', error);
      Swal.fire(
        'Erro',
        error.response?.data?.message || 'Falha ao atualizar datas',
        'error',
      )
      console.error('Erro completo:', error)
    } finally {
      setLoading(false)
    }
  }

  // Resetar modal
  const resetModal = () => {
    setFilterCode('')
    setFilterName('')
    setFilterCategory('')
    setProducts([])
    setAllSelected(false)
    setBatchDateStart('')
    setBatchDateEnd('')
    setEditMode('batch')
    setCurrentPage(1)
    setPageCount(1)
    setItemCount(0)
    setHasPreviousPage(false)
    setHasNextPage(false)
  };

  const goToPage = (page: number) => {
    if (page < 1 || page > pageCount || loading) return
    searchProducts(page)
  }

  if (!isOpen) return null;

  return (
    <div className="sale-dates-modal-overlay" onClick={onClose}>
      <div
        className="sale-dates-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sale-dates-modal-header">
          <h2>Editar Datas de Venda em Lote</h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Seção de Filtros */}
        <div className="sale-dates-filter-section">
          <h3>Filtros</h3>
          <div className="filter-inputs">
            <input
              type="text"
              placeholder="Código"
              value={filterCode}
              onChange={(e) => setFilterCode(e.target.value)}
              className="filter-input"
            />
            <input
              type="text"
              placeholder="Nome"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              className="filter-input"
            />
            <input
              type="text"
              placeholder="Categoria"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="filter-input"
            />

            <button
              onClick={handleSearch}
              disabled={loading}
              className="search-button"
            >
              {loading ? 'Buscando...' : 'Pesquisar'}
            </button>
          </div>
        </div>

        {/* Tabela de Produtos */}
        <div className="sale-dates-table-section">
          <div className="table-header">
            <label className="select-all-label">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
              />
              Selecionar Tudo
            </label>
            <span className="product-count">
              {products.length > 0
                ? `${products.filter((p) => p.selected).length} de ${products.length} selecionado(s)`
                : 'Nenhum produto encontrado'}
            </span>
          </div>

          <div className="sale-dates-table-wrapper">
            <table className="sale-dates-table">
              <thead>
                <tr>
                  <th>Selecionar</th>
                  <th>Código</th>
                  <th>Nome</th>
                  <th>Categoria</th>
                  {editMode === 'individual' && (
                    <>
                      <th>Data Início</th>
                      <th>Data Término</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {products.length > 0 ? (
                  products.map((product, index) => (
                    <tr key={product.id} className={product.selected ? 'selected' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={product.selected || false}
                          onChange={() => toggleProductSelection(index)}
                        />
                      </td>
                      <td>{product.code}</td>
                      <td>{product.name}</td>
                      <td>{product.categoryName || '-'}</td>
                      {editMode === 'individual' && (
                        <>
                          <td>
                            <input
                              type="text"
                              placeholder="DD/MM/YYYY HH:MM"
                              value={product.saleDateStart || ''}
                              onChange={(e) =>
                                updateProductDate(
                                  index,
                                  'saleDateStart',
                                  applyDateTimeMask(e.target.value),
                                )
                              }
                              maxLength={16}
                              disabled={!product.selected}
                              className="date-input"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              placeholder="DD/MM/YYYY HH:MM"
                              value={product.saleDateEnd || ''}
                              onChange={(e) =>
                                updateProductDate(
                                  index,
                                  'saleDateEnd',
                                  applyDateTimeMask(e.target.value),
                                )
                              }
                              maxLength={16}
                              disabled={!product.selected}
                              className="date-input"
                            />
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={editMode === 'individual' ? 6 : 4} className="empty-message">
                      Nenhum produto encontrado. Use os filtros para buscar.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="sale-dates-pagination">
            <button
              className="pagination-button"
              onClick={() => goToPage(1)}
              disabled={!hasPreviousPage || loading}
            >
              « Primeiro
            </button>
            <button
              className="pagination-button"
              onClick={() => goToPage(currentPage - 1)}
              disabled={!hasPreviousPage || loading}
            >
              ‹ Anterior
            </button>
            <span className="pagination-info">
              Página {currentPage} de {pageCount} • {itemCount} itens
            </span>
            <button
              className="pagination-button"
              onClick={() => goToPage(currentPage + 1)}
              disabled={!hasNextPage || loading}
            >
              Próxima ›
            </button>
            <button
              className="pagination-button"
              onClick={() => goToPage(pageCount)}
              disabled={!hasNextPage || loading}
            >
              Última »
            </button>
          </div>
        </div>

        {/* Seção de Edição */}
        <div className="sale-dates-edit-section">
          <h3>Modo de Edição</h3>
          <div className="edit-mode-options">
            <label>
              <input
                type="radio"
                value="batch"
                checked={editMode === 'batch'}
                onChange={(e) => setEditMode(e.target.value as 'batch' | 'individual')}
              />
              Aplicar para Todos os Selecionados
            </label>
            <label>
              <input
                type="radio"
                value="individual"
                checked={editMode === 'individual'}
                onChange={(e) => setEditMode(e.target.value as 'batch' | 'individual')}
              />
              Editar Individuais
            </label>
          </div>

          {editMode === 'batch' && (
            <div className="batch-dates-inputs">
              <div className="date-group">
                <label>Data de Início da Venda (DD/MM/YYYY HH:MM):</label>
                <input
                  type="text"
                  placeholder="DD/MM/YYYY HH:MM"
                  value={batchDateStart}
                  onChange={(e) => setBatchDateStart(applyDateTimeMask(e.target.value))}
                  maxLength={16}
                  className="batch-date-input"
                />
              </div>
              <div className="date-group">
                <label>Data de Término da Venda (DD/MM/YYYY HH:MM):</label>
                <input
                  type="text"
                  placeholder="DD/MM/YYYY HH:MM"
                  value={batchDateEnd}
                  onChange={(e) => setBatchDateEnd(applyDateTimeMask(e.target.value))}
                  maxLength={16}
                  className="batch-date-input"
                />
              </div>
            </div>
          )}
        </div>

        {/* Botões de Ação */}
        <div className="sale-dates-modal-footer">
          <button onClick={onClose} className="button button-secondary">
            Cancelar
          </button>
          <button
            onClick={updateDates}
            disabled={loading || products.filter((p) => p.selected).length === 0}
            className="button button-primary"
          >
            {loading ? 'Atualizando...' : 'Atualizar Datas'}
          </button>
        </div>
      </div>
    </div>
  );
};
